/**
 * แกนถอด QR จากภาพสลิป — **ฟังก์ชันบริสุทธิ์**
 *
 * ไฟล์นี้ห้าม import โมดูลเฉพาะ Node (fs, path, sharp, canvas) เด็ดขาด
 * เพราะ Sprint 3 (LIFF บนมือถือ) ต้องเรียก `decodeSlipQr` ตัวเดียวกันนี้ในเบราว์เซอร์
 * โค้ดอ่านไฟล์และแปลงภาพอยู่ที่ `src/node/` แยกต่างหาก
 *
 * ผลลัพธ์เป็นข้อมูล **ชั้น A เท่านั้น** (SOT §6 กฎเหล็กข้อ 8)
 * และ `qr_ok` คือ "อ่าน QR ได้" ไม่ใช่ "ตรวจแล้ว" (กฎเหล็กข้อ 4)
 *
 * ตัวถอดจริง (jsQR) เสียบเข้ามาผ่านสัญญา `QrEngine` (`./engines/types.ts`)
 * ไฟล์นี้ไม่รู้จักชื่อไลบรารีใดๆ นอกจาก engine เริ่มต้น (jsQR) ที่ตั้งไว้เพื่อไม่ให้
 * พฤติกรรมเดิมของผู้เรียกที่ไม่ได้ระบุ engine เปลี่ยนไป — engine ตัวที่สองที่เคยมีถูกถอดออกแล้ว
 * (ดู `FORMATS.md` หัวข้อ 6) แต่สัญญา `QrEngine` เดิมยังอยู่ครบ
 *
 * **ไม่ครอบ `engine.decode()` ด้วย try/catch ที่นี่** — engine พัง (เช่น WASM โหลดไม่สำเร็จ)
 * ต้อง throw ออกไปหาผู้เรียกโดยตรง ห้ามกลืนแล้วกลายเป็น "อ่านไม่ออก" ปกติ และ**ห้ามมี
 * logic ใดๆ ในไฟล์นี้ที่สลับไปเรียก engine ตัวอื่นเมื่อ engine ที่ขอมาพัง** — การเลือก engine
 * เป็นสิทธิ์ของผู้เรียก (ดู `./engines/types.ts` สำหรับรายละเอียดสัญญา)
 *
 * **สัญญาเดียวกันนี้ใช้กับชั้นเตรียมภาพ (`stage.build()`) ด้วย** — เดิมไฟล์นี้มี
 * `try { candidates = stage.build(img); } catch { continue; }` ซึ่งกลืน exception ของชั้น
 * เตรียมภาพเป็น "ชั้นนี้ไม่มีผู้เข้าชิง" เงียบๆ ไม่ต่างจาก "ภาพนี้ไม่มี QR" ทำให้ถ้าชั้นไหนพังจริง
 * (เช่น คำนวณขนาดผิดจนขอหน่วยความจำเกิน) จะไม่มีใครรู้ (`handoff.md` หนี้ข้อ 9) ตอนนี้
 * `decodeQrRaw()` **ยังลองชั้นถัดไปต่อเหมือนเดิมทุกประการ** (พฤติกรรมการถอดไม่เปลี่ยน — ชั้นอื่น
 * อาจถอดได้อยู่ดี) แต่ถ้าสุดท้ายไม่มีชั้นไหนถอดได้เลย **และมีชั้นที่พังจริงอย่างน้อยหนึ่งชั้น**
 * จะ throw สาเหตุจริงของชั้นล่าสุดที่พัง แทนที่จะคืน `null` (= `no-qr-found`) เงียบๆ — ผู้เรียก
 * (`src/node/index.ts`) จับ throw นี้แล้วแปลงเป็น `reason: "read-failed"` พร้อมข้อความจริง
 */

import type { ImageDataLike, QrDecodeResult } from "./types.js";
import { parsePayload } from "./parse-payload.js";
import { createJsQrEngine } from "./engines/jsqr-engine.js";
import type { QrEngine, QrEngineInvert } from "./engines/types.js";
import {
  adaptiveBinarize,
  fitWithin,
  isUsableImage,
  otsuBinarize,
  scaleUp,
  stretchContrast,
  tiles,
} from "./preprocess.js";

type Inversion = "dontInvert" | "onlyInvert" | "attemptBoth";

const toEngineInvert = (inv: Inversion): QrEngineInvert =>
  inv === "onlyInvert" ? "only" : inv === "attemptBoth" ? "both" : "none";

interface Stage {
  name: string;
  /** สร้างภาพที่จะป้อน jsQR — คืนหลายภาพได้ (เช่นการแบ่ง tile) */
  build: (img: ImageDataLike) => ImageDataLike[];
  inversion: Inversion;
}

/**
 * ขนาดที่ LIFF จะย่อภาพก่อนอัปโหลด (SPRINTS Sprint 3: "ย่อ 1568px")
 * ตั้งชั้นแรกให้ตรงกับของจริง เพื่อให้ตัวเลข hit rate ที่วัดได้ตรงกับที่หน้างานจะเจอ
 */
const PRODUCTION_MAX_DIM = 1568;

/**
 * ลำดับชั้นเรียงจากถูกไปแพง คืนทันทีที่ชั้นไหนถอดได้
 * ชั้นท้ายๆ (tile) แพงมาก จึงทำเฉพาะเมื่อชั้นทั้งภาพล้มเหลว
 */
const STAGES: Stage[] = [
  {
    name: "fit1568",
    build: (img) => [fitWithin(img, PRODUCTION_MAX_DIM)],
    inversion: "dontInvert",
  },
  {
    name: "fit1568-invert",
    build: (img) => [fitWithin(img, PRODUCTION_MAX_DIM)],
    inversion: "onlyInvert",
  },
  {
    name: "native",
    build: (img) => (Math.max(img.width, img.height) > PRODUCTION_MAX_DIM ? [img] : []),
    inversion: "attemptBoth",
  },
  {
    name: "fit1024",
    build: (img) => [fitWithin(img, 1024)],
    inversion: "attemptBoth",
  },
  {
    name: "fit1568-contrast",
    build: (img) => [stretchContrast(fitWithin(img, PRODUCTION_MAX_DIM))],
    inversion: "attemptBoth",
  },
  {
    name: "fit1568-otsu",
    build: (img) => [otsuBinarize(fitWithin(img, PRODUCTION_MAX_DIM))],
    inversion: "attemptBoth",
  },
  {
    name: "fit1568-adaptive",
    build: (img) => [adaptiveBinarize(fitWithin(img, PRODUCTION_MAX_DIM))],
    inversion: "attemptBoth",
  },
  {
    name: "upscale2x",
    build: (img) => (Math.max(img.width, img.height) < 900 ? [scaleUp(img, 2)] : []),
    inversion: "attemptBoth",
  },
  {
    // QR เล็กอยู่มุมสลิปในภาพถ่ายใหญ่ — ตัดเป็นตารางแล้วอ่านทีละช่องที่ความละเอียดจริง
    name: "tile3x3",
    build: (img) => tiles(fitWithin(img, 2400), 3, 3, 0.25),
    inversion: "dontInvert",
  },
  {
    name: "tile3x3-adaptive",
    build: (img) => tiles(fitWithin(img, 2400), 3, 3, 0.25).map((t) => adaptiveBinarize(t)),
    inversion: "attemptBoth",
  },
];

/** ชั้นที่รันเสมอ ส่วนที่เหลือรันเมื่อ `deep` เป็น true */
const CHEAP_STAGES = 4;

/** engine เริ่มต้นเมื่อผู้เรียกไม่ระบุ — คงพฤติกรรมเดิมของทุกจุดที่เรียก `decodeSlipQr` อยู่แล้วไว้ */
const DEFAULT_ENGINE: QrEngine = createJsQrEngine();

export interface DecodeOptions {
  /** false = หยุดที่ชั้นถูกๆ เท่านั้น (ใช้ตอนถ่ายสดบนมือถือที่ต้องตอบเร็ว) */
  deep?: boolean;
  /** ตัวถอดที่จะใช้ — ค่าเริ่มต้น jsQR */
  engine?: QrEngine;
}

/** ถอดเฉพาะสตริงดิบจาก QR ไม่แยก payload — เผื่อกรณีอยาก dump ดูรูปแบบใหม่ */
export async function decodeQrRaw(
  img: ImageDataLike,
  opts: DecodeOptions = {},
): Promise<{ raw: string; stage: string } | null> {
  if (!isUsableImage(img)) return null;
  const engine = opts.engine ?? DEFAULT_ENGINE;
  const stages = opts.deep === false ? STAGES.slice(0, CHEAP_STAGES) : STAGES;

  /**
   * ชั้นเตรียมภาพล่าสุดที่ `build()` เอง throw จริง — เก็บไว้เฉยๆ ไม่หยุดลูป (ชั้นถัดไปอาจ
   * ยังถอดได้อยู่ดี พฤติกรรมการถอดไม่เปลี่ยนจากเดิม) ใช้ก็ต่อเมื่อสุดท้ายไม่มีชั้นไหนถอดได้เลย
   * เก็บแค่ตัวล่าสุดพอ — เป้าหมายคือมีสาเหตุจริงสักตัวให้ throw ไม่ใช่สะสมประวัติทั้งหมด
   */
  let stageFailure: { stage: string; error: unknown } | null = null;

  for (const stage of stages) {
    let candidates: ImageDataLike[];
    try {
      candidates = stage.build(img);
    } catch (e) {
      // ชั้นเตรียมภาพพังจริง (บั๊กของเราเอง เช่น คำนวณขนาดจนขอหน่วยความจำเกิน) ไม่ใช่
      // "ชั้นนี้ไม่มีผู้เข้าชิง" — เก็บสาเหตุไว้แล้วลองชั้นถัดไปต่อ (ดูคอมเมนต์หัวไฟล์)
      stageFailure = { stage: stage.name, error: e };
      continue;
    }
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i]!;
      if (c.width < 20 || c.height < 20) continue;
      // ห้าม try/catch คลุม engine.decode ตรงนี้ — ตามสัญญาใน `./engines/types.ts`
      // engine คืน null เองอยู่แล้วเมื่อ "ภาพนี้ไม่มี QR" ส่วน exception ที่หลุดออกมา
      // (เช่น engine ที่ใช้ WASM โหลดไฟล์ไม่สำเร็จ) คือ engine พังทั้งตัว ต้องปล่อยให้ผู้เรียกเห็น
      // ไม่ใช่กลืนแล้วรายงานเป็น "อ่านไม่ออก" ปกติ — นั่นคือ fallback เงียบๆ ที่ห้ามมี
      const raw = await engine.decode(c, { invert: toEngineInvert(stage.inversion) });
      if (raw && raw.length > 0) {
        const label = candidates.length > 1 ? `${stage.name}#${i}` : stage.name;
        return { raw, stage: label };
      }
    }
  }

  if (stageFailure) {
    const cause = stageFailure.error instanceof Error ? stageFailure.error : undefined;
    throw new Error(
      `เตรียมภาพสำหรับชั้น "${stageFailure.stage}" ล้มเหลว: ` +
        (cause?.message ?? String(stageFailure.error)),
      cause ? { cause } : undefined,
    );
  }
  return null;
}

/**
 * ถอด QR จากภาพหนึ่งใบ → ข้อมูลชั้น A
 *
 * `qr_ok` เป็น true ก็ต่อเมื่อถอด QR ได้ **และ** แยก `trans_ref` ออกมาได้
 * เพราะถ้าไม่มี `trans_ref` ก็สร้าง `ref_hash` ไม่ได้ กันสลิปซ้ำไม่ได้ ท่อขั้นถัดไปใช้งานไม่ได้จริง
 */
export async function decodeSlipQr(img: ImageDataLike, opts: DecodeOptions = {}): Promise<QrDecodeResult> {
  if (!isUsableImage(img)) {
    return {
      qr_ok: false,
      bank_code: null,
      trans_ref: null,
      raw: null,
      format: "none",
      reason: "bad-image",
    };
  }

  const found = await decodeQrRaw(img, opts);
  if (!found) {
    return {
      qr_ok: false,
      bank_code: null,
      trans_ref: null,
      raw: null,
      format: "none",
      reason: "no-qr-found",
    };
  }

  const parsed = parsePayload(found.raw);
  if (parsed.trans_ref === null) {
    return {
      qr_ok: false,
      bank_code: parsed.bank_code,
      trans_ref: null,
      raw: found.raw,
      format: parsed.format,
      // **ส่งต่อเหตุผลจริงจากตัวแยก payload ตรงตัว** ห้ามแปลงเป็นค่าอื่น
      // `not-slip-payload` เป็นค่าเริ่มต้นสำหรับกรณีเดียวเท่านั้น: ตัวแยกหา trans_ref ไม่เจอ
      // และ **ไม่รู้สาเหตุที่ชัดกว่านั้น** (เนื้อหาไม่มีร่องรอยของโครง QR ตรวจสอบสลิปเลย —
      // ดู `format` ประกอบเพื่อรู้ว่าเป็น URL / TLV ที่ tag ไม่ตรง / ข้อความอื่น ดู types.ts)
      // ถ้าตัวแยกบอกสาเหตุมาแล้ว (เช่น `crc-mismatch` = อ่าน QR มาไม่ครบดวง) การเขียนทับ
      // ด้วย `not-slip-payload` คือการรายงานผิด ซึ่งอันตรายกว่าไม่รายงานเลย เพราะคนที่มา
      // ไล่ปัญหาจะไปแก้ตัวแยกรูปแบบ ทั้งที่ต้นเหตุอยู่ที่คุณภาพภาพ
      // (บั๊กสายพันธุ์เดียวกับที่ไฟล์นี้เคยกลืน error ของ engine เป็น null)
      reason: parsed.reason ?? "not-slip-payload",
      stage: found.stage,
    };
  }

  return {
    qr_ok: true,
    bank_code: parsed.bank_code,
    trans_ref: parsed.trans_ref,
    raw: found.raw,
    format: parsed.format,
    stage: found.stage,
  };
}
